import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getFilteredOrdersByItemName,
  getFilteredOrdersByHouseholdNumber,
  getCurrentOrders,
  getHistoryOrders,
  getCascaderOptions,
  groupBuyingOrdersContextDefaultValues,
  tabsItems,
} from './helper';
import { Cascader, Input, Space, Tabs } from 'antd';

import type { GroupBuying } from '@/types/database/groupBuying';
import type { User } from '@/types/database/users';
import type { GroupBuyingOrders } from '@/types/database/groupBuyingOrders';
import useService from '@/lib/hooks/useService';

import AddOrder from '../AddOrder';
import styles from './index.module.scss';

export const GroupBuyingOrdersContext = createContext(
  groupBuyingOrdersContextDefaultValues
);

const OrderManagement = () => {
  const [groupBuyingItems] = useService<GroupBuying>({
    collectionName: 'groupBuying',
  });
  const [groupBuyingOrdersFromDatabase, updateOrders] =
    useService<GroupBuyingOrders>({
      collectionName: 'groupBuyingOrders',
    });
  const [users] = useService<User>({ collectionName: 'users' });

  const [groupBuyingOrders, setGroupBuyingOrders] = useState<
    GroupBuyingOrders[]
  >(groupBuyingOrdersFromDatabase);
  const [tab, setTab] = useState<'現在團購' | '歷史資料'>('現在團購');
  const [selectedHouseholdNumber, setSelectedHouseholdNumber] = useState<
    string[]
  >([]);
  const [searchedGroupBuyingItemName, setSearchedGroupBuyingItemName] =
    useState('');
  const [isItemNameSearched, setIsItemNameSearched] = useState(false);

  useEffect(() => {
    setGroupBuyingOrders(groupBuyingOrdersFromDatabase);
  }, [groupBuyingOrdersFromDatabase]);

  const updateTableData = useCallback(
    (itemName: string, householdNumber: string[]) => {
      let filteredOrders: GroupBuyingOrders[] = groupBuyingOrdersFromDatabase;

      if (itemName) {
        filteredOrders = getFilteredOrdersByItemName({
          groupBuyingOrders: groupBuyingOrdersFromDatabase,
          groupBuyingItems,
          itemName,
        });
      }
      if (householdNumber?.length > 0) {
        filteredOrders = getFilteredOrdersByHouseholdNumber({
          users,
          groupBuyingOrders: filteredOrders,
          householdNumber,
        });
      }

      setGroupBuyingOrders(filteredOrders);
    },
    [groupBuyingItems, groupBuyingOrdersFromDatabase, users]
  );

  useEffect(() => {
    if (isItemNameSearched) {
      updateTableData(searchedGroupBuyingItemName, selectedHouseholdNumber);
      setIsItemNameSearched(false);
    }
  }, [
    isItemNameSearched,
    searchedGroupBuyingItemName,
    selectedHouseholdNumber,
    updateTableData,
  ]);

  const handleInputChange = (searchedValue: string) => {
    setSearchedGroupBuyingItemName(searchedValue);
    if (searchedValue === '') {
      setIsItemNameSearched(true);
    }
  };

  const searchGroupBuyingOrders = (searchedValue: string) => {
    setSearchedGroupBuyingItemName(searchedValue);
    setIsItemNameSearched(true);
    if (searchedValue === '') {
      setGroupBuyingOrders(groupBuyingOrdersFromDatabase);
    }
  };

  const filterByHouseholdNumber = (householdNumber: string[]) => {
    setSelectedHouseholdNumber(householdNumber);
    setIsItemNameSearched(true);
    if (householdNumber === undefined) {
      updateTableData(searchedGroupBuyingItemName, householdNumber);
    }
  };

  const allOrders = useMemo(
    () => ({
      currentOrders: getCurrentOrders(groupBuyingOrders, groupBuyingItems),
      historyOrders: getHistoryOrders(groupBuyingOrders, groupBuyingItems),
      updateOrders,
    }),
    [groupBuyingItems, groupBuyingOrders, updateOrders]
  );

  return (
    <GroupBuyingOrdersContext.Provider value={allOrders}>
      <Space size="middle" direction="vertical" className={styles.container}>
        <Space direction="vertical" size="middle">
          <AddOrder />
          <Space>
            <Space>
              <span>團購:</span>
              <Input
                allowClear
                onChange={(event) =>
                  handleInputChange(event.currentTarget.value)
                }
                onPressEnter={(event) =>
                  searchGroupBuyingOrders(event.currentTarget.value)
                }
                placeholder="請輸入團購名稱"
                className={styles.searchInput}
                value={searchedGroupBuyingItemName}
              />
            </Space>
            <Space>
              <span>戶號:</span>
              <Cascader
                placeholder="請選擇戶號"
                options={getCascaderOptions({
                  tab,
                  users,
                  groupBuyingOrders,
                  groupBuyingItems,
                })}
                onChange={filterByHouseholdNumber}
                value={selectedHouseholdNumber}
              />
            </Space>
          </Space>
        </Space>
        <Tabs
          items={tabsItems}
          onChange={(activeKey: '現在團購' | '歷史資料') => setTab(activeKey)}
        />
      </Space>
    </GroupBuyingOrdersContext.Provider>
  );
};

export default OrderManagement;
